import { useEffect, useMemo, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import logoAnimationData from './animations/logo_animation.json';

const FADE_OUT_DURATION = 500;
const DESC_TRANSITION_DURATION = 300;
const FALLBACK_INITIAL_DELAY = 3000;
const FALLBACK_INTERVAL = 4000;

type MainLoaderProps = {
  isVisible: boolean;
  description?: string | null;
  staticLogo?: boolean;
  fallbackMessages?: string[];
  logoSrc?: string;
  className?: string;
};

export const MainLoader: React.FC<MainLoaderProps> = ({
  isVisible,
  description,
  staticLogo,
  fallbackMessages,
  logoSrc = './assets/images/logo-on-white-200X80.svg',
  className,
}) => {
  const [isMounted, setMounted] = useState(isVisible);
  const [isFadingOut, setFadingOut] = useState(false);
  const [activeDescription, setActiveDescription] = useState<string | null>(
    description ?? null,
  );
  const [isDescAnimatingOut, setDescAnimatingOut] = useState(false);

  const fallbackList = useMemo(
    () => (fallbackMessages ?? []).filter(Boolean),
    [fallbackMessages],
  );
  const timersRef = useRef<{
    hideTimeout?: ReturnType<typeof setTimeout>;
    initialTimeout?: ReturnType<typeof setTimeout>;
    interval?: ReturnType<typeof setInterval>;
    descTimeout?: ReturnType<typeof setTimeout>;
  }>({});

  useEffect(() => {
    if (isVisible) {
      setMounted(true);
      setFadingOut(false);
    } else if (isMounted) {
      setFadingOut(true);
      timersRef.current.hideTimeout = setTimeout(() => {
        setMounted(false);
        setFadingOut(false);
      }, FADE_OUT_DURATION);
    }

    return () => {
      if (timersRef.current.hideTimeout) {
        clearTimeout(timersRef.current.hideTimeout);
      }
    };
  }, [isVisible, isMounted]);

  useEffect(() => {
    const clearCycleTimers = () => {
      const { initialTimeout, interval, descTimeout } = timersRef.current;
      if (initialTimeout) { clearTimeout(initialTimeout); }
      if (interval) { clearInterval(interval); }
      if (descTimeout) { clearTimeout(descTimeout); }
      timersRef.current.initialTimeout = undefined;
      timersRef.current.interval = undefined;
      timersRef.current.descTimeout = undefined;
    };

    clearCycleTimers();

    if (description) {
      setActiveDescription(description);
      return;
    }

    if (!isMounted || fallbackList.length === 0) {
      setActiveDescription(null);
      return;
    }

    let index = 0;
    setActiveDescription(fallbackList[index]);

    timersRef.current.initialTimeout = setTimeout(() => {
      timersRef.current.interval = setInterval(() => {
        setDescAnimatingOut(true);
        timersRef.current.descTimeout = setTimeout(() => {
          index = (index + 1) % fallbackList.length;
          setActiveDescription(fallbackList[index]);
          setDescAnimatingOut(false);
        }, DESC_TRANSITION_DURATION);
      }, FALLBACK_INTERVAL);
    }, FALLBACK_INITIAL_DELAY);

    return clearCycleTimers;
  }, [description, fallbackList, isMounted]);

  if (!isMounted) {
    return null;
  }

  const containerBase =
    'fixed inset-0 z-[9999] flex flex-col items-center justify-center px-6 transition-opacity duration-500 ease-linear';
  const staticLogoClasses =
    'bg-white text-[#1f2a44]';
  const animatedLogoClasses =
    'bg-gradient-to-r from-[#FFEDEE] to-[#E1E7FD] text-[#4B5356]';

  return (
    <div
      className={[
        containerBase,
        staticLogo ? staticLogoClasses : animatedLogoClasses,
        isFadingOut ? 'opacity-0 pointer-events-none' : 'opacity-100',
        className ?? '',
      ].join(' ').trim()}
    >
      {staticLogo ? (
        <div className="flex flex-col items-center text-center">
          <img
            src={logoSrc}
            alt="Creately"
            className="mb-6 -mr-2 h-auto w-32"
            width={130}
          />
          <div className="overflow-hidden">
            <div className="flex items-center justify-center">
              <div className="balls">
                <div className="h-4 w-4 rounded-full bg-[#1f2a44]"></div>
                <div className="h-4 w-4 rounded-full bg-[#1f2a44]"></div>
                <div className="h-4 w-4 rounded-full bg-[#1f2a44]"></div>
              </div>
            </div>
            {activeDescription && (
              <div className="mt-5">
                <span className="block text-xs font-medium text-[#1f2a44]">
                  {activeDescription}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center text-center">
          <Lottie
            animationData={logoAnimationData}
            loop
            autoplay
            className="max-w-xs"
          />
          {activeDescription && (
            <span
              className={[
                'mt-3 text-xs font-medium transition-all duration-300 ease-out text-[#4B5356]',
                isDescAnimatingOut
                  ? 'opacity-0 -translate-y-2'
                  : 'opacity-100 translate-y-0',
              ].join(' ')}
            >
              {activeDescription}
            </span>
          )}
        </div>
      )}
    </div>
  );
};